<template>
  <div id="container" class="container">
    <MapSearchBar @search="handleSearch" />
    <div id="searchResults">
      <div
        id="searchResultsHeader"
        v-if="this.foundSearchableObjects.length != this.searchableObjects.length"
      >
        {{
          $tc('map_detail.search_panel.x_results_found', this.foundSearchableObjects.length, {
            nb_results: this.foundSearchableObjects.length,
          })
        }}
      </div>
      <ul>
        <MapSearchResult
          v-for="(objSet, idx) in this.usableSets"
          :key="`${objSet.id}-${idx}`"
          :objSet="objSet"
          @selected="onSelected"
          @toggle="toggleVisibility"
        />
      </ul>
    </div>
  </div>
</template>

<script>
import MapSearchBar from './MapSearchBar.vue';
import MapSearchResult from './MapSearchResult.vue';
import * as tinycolor from 'tinycolor2';
import LangUtils from '../../../utils/LangUtils';
import StringUtils from '@/utils/StringUtils';
import GeomUtils from '@/utils/GeomUtils';
import MapObjectAnnotation from '@/map/element/MapObjectAnnotation';
import MapObjectShape from '@/map/element/MapObjectShape';
import MapObjectLayer from '@/map/element/MapObjectLayer';

const sortOnName = (a, b) => {
  return StringUtils.compare(a.getName().toLowerCase(), b.getName().toLowerCase());
};
const sortOnDistance = (a, b) => {
  if (a.distance && b.distance) {
    if (a.distance === b.distance) {
      return 0;
    }
    if (b.distance < a.distance) {
      return 1;
    }
  }
  return -1;
};

export default {
  components: {
    MapSearchBar,
    MapSearchResult,
  },
  name: 'MapSearchPanel',
  data: () => {
    return {
      foundSearchableObjects: [],
    };
  },
  props: {
    projection: { type: Object, default: () => null },
    searchableObjects: Array,
    mapObjectStatusTemplates: null,
    allMapObjectStatus: null,
    searchPanelStructure: null,
    sortMethod: { type: String, default: () => null },
  },
  watch: {
    searchableObjects: function () {
      this.foundSearchableObjects = this.searchableObjects;
    },

    allMapObjectStatus: function () {
      if (this.allMapObjectStatus) {
        this.$forceUpdate();
      }
    },
  },
  computed: {
    flatSets() {
      return this.foundSearchableObjects.map((parent) => this.makeSet(parent));
    },
    structuredSets() {
      const locale = this.$i18n.locale;
      const result = [];
      for (const struct of this.searchPanelStructure) {
        let sectionResults = [];
        const sectionName = struct.name ? struct.name[locale] : struct.tagId;
        const section = {
          name: sectionName,
          classes: { [struct.tagId]: true, section: true },
        };
        if (struct.children) {
          for (const childStruct of struct.children) {
            const subSectionName = childStruct.name ? childStruct.name[locale] : childStruct.tagId;
            const subSection = {
              name: subSectionName,
              classes: { [childStruct.tagId]: true, 'sub-section': true },
            };

            const matches = this.foundSearchableObjects
              .filter((x) => {
                const tags = x.getTags();
                if (tags) {
                  return childStruct.tagId in tags && struct.tagId in tags;
                } else {
                  return false;
                }
              })
              .map(this.makeSet.bind(this));
            if (matches.length > 0) {
              // Only add sub-section if it has items
              sectionResults.push(subSection, ...matches);
            }
          }
        } else {
          sectionResults = this.foundSearchableObjects
            .filter((x) => struct.tagId in x.getTags())
            .map(this.makeSet.bind(this));
        }
        if (sectionResults.length > 0) {
          // Only add section if it has items
          result.push(section, ...sectionResults);
        }
      }
      return result;
    },
    usableSets() {
      return this.searchPanelStructure ? this.structuredSets : this.flatSets;
    },
  },
  methods: {
    getSearchableObjectStyle(searchableObject) {
      const mapElement = searchableObject.mapElement;
      const status = this.findMapObjectStatusForMapElement(mapElement.id);

      return searchableObject.getType() == 'layer'
        ? 'section'
        : status
        ? 'item item-with-status'
        : 'item item-without-status';
    },

    statusStyle(searchableObject) {
      const mapElement = searchableObject.mapElement;
      const status = this.findMapObjectStatusForMapElement(mapElement.id);

      if (status) {
        const template = this.findMapObjectStatusTemplate(status.mapObjectStatusTemplateId);
        if (template) {
          var fontColor = '#000000';
          if (tinycolor(template.color).getBrightness() > 205) {
            fontColor = tinycolor(template.color).darken(100);
          } else {
            fontColor = tinycolor(template.color).lighten(100);
          }
          return 'background-color: ' + template.color + '; color: ' + fontColor;
        } else {
          return '';
        }
      } else {
        return '';
      }
    },

    statusString(searchableObject) {
      const mapElement = searchableObject.mapElement;
      const status = this.findMapObjectStatusForMapElement(mapElement.id);

      if (status) {
        const template = this.findMapObjectStatusTemplate(status.mapObjectStatusTemplateId);
        if (template) {
          return LangUtils.valueForLanguage(template.title, this.$i18n.locale);
        } else {
          return '';
        }
      } else {
        return '';
      }
    },

    addDistance(reference, target) {
      if (target.subElements) {
        target.subElements.map((subElement) => this.addDistance(reference, subElement));
      }
      let distance = null;
      const { mapElement } = target;
      if (mapElement instanceof MapObjectAnnotation) {
        const dest = mapElement.mapObject.origin;
        if (dest) {
          distance = reference.getDistance(dest);
        }
      } else if (mapElement instanceof MapObjectShape) {
        const { bottomRightBound, topLeftBound } = mapElement;
        const topLeft = this.projection.locationToPoint(topLeftBound);
        const bottomRight = this.projection.locationToPoint(bottomRightBound);
        distance = GeomUtils.getDistance(topLeft, bottomRight, reference);
      }
      target.distance = distance;
      return target;
    },

    nestNaturalItems(items) {
      const result = [];
      let lastRoot = null;
      for (const item of items) {
        const { mapElement } = item;
        if (mapElement instanceof MapObjectLayer) {
          lastRoot = item;
          lastRoot.subElements = [];
          result.push(item);
        } else {
          if (lastRoot) {
            lastRoot.subElements.push(item);
          } else {
            result.push(item);
          }
        }
      }
      return result;
    },

    flattenNestedItems(items) {
      const result = [];
      for (const item of items) {
        result.push(item);
        if (item.subElements) {
          result.push(...this.flattenNestedItems(item.subElements));
        }
      }
      return result;
    },

    async handleSearch(searchedValue) {
      if (searchedValue.length == 0) {
        this.foundSearchableObjects = await this.sortResults(this.searchableObjects);
      } else {
        const foundObjects = [];
        for (const searchableObject of this.searchableObjects) {
          if (searchableObject.isSelectable()) {
            const searchableObjectName = searchableObject.getName();
            if (
              searchableObjectName &&
              searchableObjectName.toLowerCase().indexOf(searchedValue.toLowerCase()) >= 0
            ) {
              foundObjects.push(searchableObject);
            }
          }
        }
        this.foundSearchableObjects = await this.sortResults(foundObjects);
      }
    },

    async sortResults(results) {
      // making a shallow copy should be enough
      const sorted = Array.from(results);
      const nested = this.nestNaturalItems(sorted);
      if (this.sortMethod === 'alphabetically') {
        nested.sort(sortOnName);
        for (const obj of nested) {
          if (obj.subElements) {
            obj.subElements.sort(sortOnName);
          }
        }
      }
      if (this.sortMethod === 'distance') {
        try {
          const reference = await GeomUtils.getCurrentMapPosition(this.projection);
          nested.map((obj) => this.addDistance(reference, obj));
          nested.sort(sortOnDistance);
          for (const obj of nested) {
            if (obj.subElements) {
              obj.subElements.sort(sortOnDistance);
            }
          }
        } catch (error) {
          console.log('Cannot access location; skipping');
        }
      }
      return this.flattenNestedItems(nested);
    },

    onSelected(id) {
      this.$emit('searchable-object-selected', id);
    },

    toggleVisibility({ id, value }) {
      const match = this.foundSearchableObjects.find((x) => id === x.getId());
      if (match) {
        match.visible = value;
      }
    },

    findMapObjectStatusForMapElement(mapElementId) {
      if (this.allMapObjectStatus) {
        for (var mapObjectStatus of this.allMapObjectStatus) {
          if (mapObjectStatus.mapObjectId == mapElementId && mapObjectStatus.availableOnMap) {
            return mapObjectStatus;
          }
        }
      }
      return null;
    },

    findMapObjectStatusTemplate(mapObjectStatusTemplateId) {
      if (this.mapObjectStatusTemplates) {
        for (var mapObjectStatusTemplate of this.mapObjectStatusTemplates) {
          if (mapObjectStatusTemplate.id == mapObjectStatusTemplateId) {
            return mapObjectStatusTemplate;
          }
        }
      }
      return null;
    },

    makeSet(searchableObject) {
      let iconUrl;
      let fallbackIconUrl;
      const hasIcon = searchableObject.getType() !== 'layer';
      if (hasIcon) {
        try {
          iconUrl = searchableObject.getIconUrl(true);
          fallbackIconUrl = searchableObject.getIconUrl(false);
        } catch (error) {
          console.log(error);
        }
      }

      const statusStyle = this.statusStyle(searchableObject);
      const status = this.statusString(searchableObject);
      const className = this.getSearchableObjectStyle(searchableObject)
        .split(' ')
        .reduce((acc, item) => {
          acc[item] = true;
          return acc;
        }, {});
      const tags = searchableObject.getTags();

      return {
        id: searchableObject.getId(),
        name: searchableObject.getName(),
        hasIcon,
        iconUrl,
        fallbackIconUrl,
        classes: { ...tags, ...className },
        statusStyle,
        status,
        visible: searchableObject.visible,
        isHideable: searchableObject.isHideable(),
        tags,
      };
    },
  },

  mounted() {
    this.sortResults(this.searchableObjects).then((sorted) => {
      this.foundSearchableObjects = sorted;
    });
  },
};
</script>

<style lang="scss" scoped>
.container {
  display: flex;
  flex-flow: column;
  height: 100%;
  padding: 0px;
}

#searchResults {
  flex: 1 1 auto;
  height: 100%;
  overflow: scroll;
}

#searchResultsHeader {
  color: #aaaaaa;
  font-size: 12px;
  font-weight: bold;
  padding: 10px 20px;
  text-align: left;
}

#searchResults ul {
  padding: 0px;
}
</style>
